import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, FileText, Receipt } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppCtx";
import { PageHeader, Empty, Confirm } from "@/components/kit";
import { ClientDialog } from "@/components/ClientDialog";

export default function Clients() {
  const { base } = useApp();
  const nav = useNavigate();
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const [dlg, setDlg] = useState(false);

  const load = useCallback(() => api.get("/clients", { params: { q } }).then((r) => setList(r.data)), [q]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const del = async (c) => {
    try {
      await api.delete(`/clients/${c.id}`);
      toast.success("Cliente eliminado");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <PageHeader title="Clientes" subtitle={`${list.length} clientes registrados`}>
        <Button className="btn-neon" onClick={() => { setEdit(null); setDlg(true); }} data-testid="add-client-btn">
          <Plus className="w-4 h-4 mr-1" />Nuevo cliente
        </Button>
      </PageHeader>
      <div className="panel p-4 mb-4 relative">
        <Search className="w-4 h-4 absolute left-7 top-7 text-slate-500" />
        <Input className="pl-9" placeholder="Buscar por nombre, cédula, correo o teléfono" value={q} onChange={(e) => setQ(e.target.value)} data-testid="client-search-input" />
      </div>
      <div className="panel overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]" data-testid="clients-table">
          <thead>
            <tr className="text-left label-cap"><th className="p-4">Cliente</th><th>Cédula</th><th>Contacto</th><th>Dirección</th><th className="text-right pr-4">Acciones</th></tr>
          </thead>
          <tbody>
            {list.map((c) => (
              <tr key={c.id} className="border-t border-[#271E56] hover:bg-[#1A143D]/60" data-testid="client-row">
                <td className="p-4 text-white font-medium">{c.name}</td>
                <td className="mono text-slate-300">{c.id_number || "—"}</td>
                <td className="text-slate-300"><div>{c.phone}</div><div className="text-xs text-slate-400">{c.email}</div></td>
                <td className="text-slate-400">{c.address}</td>
                <td className="pr-4">
                  <div className="flex justify-end gap-1">
                    <Button size="icon" variant="ghost" title="Nueva proforma" onClick={() => nav(`${base}/proformas/nueva?cliente=${c.id}`)} data-testid="client-new-quote"><FileText className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" title="Nueva factura" onClick={() => nav(`${base}/facturas/nueva?cliente=${c.id}`)} data-testid="client-new-invoice"><Receipt className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" title="Editar" onClick={() => { setEdit(c); setDlg(true); }} data-testid="client-edit-btn"><Pencil className="w-4 h-4" /></Button>
                    <Confirm title="¿Eliminar cliente?" description={`Se eliminará "${c.name}". Sus documentos se conservan.`} onConfirm={() => del(c)} actionLabel="Eliminar">
                      <Button size="icon" variant="ghost" className="text-rose-400" title="Eliminar" data-testid="client-delete-btn"><Trash2 className="w-4 h-4" /></Button>
                    </Confirm>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!list.length && <Empty text="No hay clientes" />}
      </div>
      <ClientDialog open={dlg} onOpenChange={setDlg} client={edit} onSaved={load} />
    </div>
  );
}
